import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import mealApi from "../../../api/meal/mealApi";
import type {
  MealConfigurationData,
  MealUpdateConfigurationPayload,
} from "../../../api/meal/types/mealMonitoringType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";

interface UpdateConfigurationArgs {
  uuid: string;
  payload: MealUpdateConfigurationPayload;
}

interface MealUpdateConfigurationState {
  data: MealConfigurationData | null;
  updateLoading: boolean;
  updateError: string | null;
  requestId: string | null;
}

const initialState: MealUpdateConfigurationState = {
  data: null,
  updateLoading: false,
  updateError: null,
  requestId: null,
};

export const updateMealConfiguration = createAsyncThunk<
  MealConfigurationData,
  UpdateConfigurationArgs,
  { rejectValue: RejectPayload }
>(
  "mealUpdateConfiguration/update",
  async ({ uuid, payload }, { rejectWithValue, signal }) => {
    try {
      const response = await mealApi.updateMealConfiguration(
        uuid,
        payload,
        signal,
      );
      return response.data;
    } catch (err) {
      if (isRequestCancelled(err, signal)) throw err;

      return rejectWithValue(
        toRejectPayload(err, "Gagal menyimpan konfigurasi meal"),
      );
    }
  },
);

const mealUpdateConfigurationSlice = createSlice({
  name: "mealUpdateConfiguration",
  initialState,
  reducers: {
    resetMealUpdateConfiguration: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateMealConfiguration.pending, (state, action) => {
        state.updateLoading = true;
        state.updateError = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(updateMealConfiguration.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.updateLoading = false;
        state.data = action.payload;
        state.updateError = null;
        state.requestId = null;
      })
      .addCase(updateMealConfiguration.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.updateLoading = false;
        state.requestId = null;

        if (action.meta.aborted) {
          state.updateError = null;
          return;
        }

        state.updateError =
          action.payload?.message ?? "Gagal menyimpan konfigurasi meal";
      });
  },
});

export const { resetMealUpdateConfiguration } =
  mealUpdateConfigurationSlice.actions;

export default mealUpdateConfigurationSlice.reducer;
